/**
 * AdminSidebar — left navigation per the 5mi.uz design handoff.
 *
 * Surface column: pentagon logo, role card (lavender hex), sectioned nav
 * (Asosiy / Kontent / Sistema), collapse toggle + logout in the footer.
 * 240px expanded / 64px collapsed.
 */
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { Logo, Hex } from '@/components/kit'
import {
  LayoutDashboard, HelpCircle, Database,
  Users, BarChart2, Settings,
  BookMarked, Tag, UsersRound, CalendarDays,
  FolderOpen, MessageSquare, ClipboardList, LineChart,
  ChevronLeft, ChevronRight, LogOut, Shield, Pencil, Bot,
} from 'lucide-react'

const NAV_ITEMS = [
  { to: '/dashboard',        labelKey: 'nav.dashboard',       Icon: LayoutDashboard },
  { to: '/exams',            labelKey: 'nav.exams',           Icon: ClipboardList },
  { to: '/groups',           labelKey: 'nav.groups',          Icon: UsersRound },
  { section: 'Kontent' },
  { to: '/questions',        labelKey: 'nav.questions',       Icon: HelpCircle },
  { to: '/question-banks',   labelKey: 'nav.questionBanks',   Icon: Database },
  { to: '/topics',           labelKey: 'nav.topics',          Icon: Tag },
  { to: '/subjects',         labelKey: 'nav.subjects',        Icon: BookMarked, adminOnly: true },
  { to: '/grading-criteria', labelKey: 'nav.gradingCriteria', Icon: BarChart2 },
  { to: '/materials',        labelKey: 'nav.materials',       Icon: FolderOpen },
  { section: 'Sistema' },
  { to: '/users',            labelKey: 'nav.users',           Icon: Users,    adminOnly: true },
  { to: '/ai-logs',         labelKey: 'nav.aiLogs',          Icon: Bot,      adminOnly: true },
  { to: '/schedule',         labelKey: 'nav.schedule',        Icon: CalendarDays },
  { to: '/forum',            labelKey: 'nav.forum',           Icon: MessageSquare },
  { to: '/settings',         labelKey: 'nav.settings',        Icon: Settings },
]

export default function Sidebar({ collapsed, onToggle }) {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const location = useLocation()

  const isAdmin = user?.is_superuser || user?.role === 'admin'
  const navItems = NAV_ITEMS.filter((item) => item.section || !item.adminOnly || isAdmin)

  return (
    <aside
      className={['sidebar-transition flex flex-col h-full flex-shrink-0 select-none', collapsed ? 'w-16' : 'w-[240px]'].join(' ')}
      style={{
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        padding: collapsed ? '16px 8px' : '20px 14px',
      }}
    >
      {/* Logo */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        padding: collapsed ? '0 0 12px' : '4px 8px 12px',
      }}>
        <Logo size={28} compact={collapsed} />
      </div>

      {/* Role card */}
      {!collapsed && (
        <div style={{
          padding: '10px 12px', borderRadius: 14, marginBottom: 6,
          background: 'var(--bg-soft)', border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10, minWidth: 0,
        }}>
          <Hex size="sm" bg="var(--lav-soft)" fg="var(--lav)" icon={isAdmin ? Shield : Pencil} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="fw-7 t-sm" style={{ lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.full_name || user?.username}
            </div>
            <div className="t-xs t-mut" style={{ marginTop: 2 }}>
              {isAdmin ? 'Admin' : t('roles.teacher', "O'qituvchi")}
            </div>
          </div>
        </div>
      )}
      {collapsed && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 12px' }} title={user?.full_name || user?.username}>
          <Hex size="sm" bg="var(--lav-soft)" fg="var(--lav)" icon={isAdmin ? Shield : Pencil} />
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scroll-y" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {navItems.map((item) =>
          item.section ? (
            collapsed ? (
              <div key={item.section} style={{ height: 1, background: 'var(--border)', margin: '10px 8px' }} />
            ) : (
              <div key={item.section} className="t-xs fw-7" style={{
                color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.08em',
                padding: '14px 14px 6px',
              }}>
                {item.section} <span style={{ color: 'var(--coral)' }}>.</span>
              </div>
            )
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              title={collapsed ? t(item.labelKey, item.fallback) : undefined}
              className={'nav-item' + (location.pathname.startsWith(item.to) ? ' active' : '')}
              style={collapsed ? { justifyContent: 'center', padding: 12 } : undefined}
            >
              <item.Icon size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
              {!collapsed && <span style={{ flex: 1 }}>{t(item.labelKey, item.fallback)}</span>}
            </NavLink>
          ),
        )}
      </nav>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button
          onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="nav-item"
          style={{ justifyContent: 'center', border: 0, background: 'transparent', font: 'inherit' }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        <button
          onClick={logout}
          title="Sign out"
          className="nav-item"
          style={{
            border: 0, background: 'transparent', font: 'inherit', width: '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <LogOut size={16} />
          {!collapsed && <span>{t('auth.logout', 'Chiqish')}</span>}
        </button>
      </div>
    </aside>
  )
}
